import { useUnsavedChanges } from '@/hooks/useUnsavedChanges';
import { useEffect, useMemo, useState } from 'react';
import { hasPermission } from '@/types';
import { useNavigate } from 'react-router-dom';
import {
  employees as api, companies as companiesApi, users as usersApi,
  type AssignableUser,
} from '@/api/endpoints';
import type { Employee, EmployeeInput, EmploymentStatus, EmployeeMeta, Company } from '@/types';
import { Icon } from '@/components/Icon';
import { SearchableSelect } from '@/components/SearchableSelect';
import { useAuth } from '@/auth/AuthContext';

const STATUSES: { id: EmploymentStatus; name: string }[] = [
  { id: 'ACTIVE', name: 'Zatrudnieni' },
  { id: 'SUSPENDED', name: 'Zawieszeni' },
  { id: 'TERMINATED', name: 'Zwolnieni' },
];

const STATUS_PILL: Record<EmploymentStatus, string> = {
  ACTIVE: 'bg-green-100 text-green-700',
  SUSPENDED: 'bg-amber-100 text-amber-700',
  TERMINATED: 'bg-ink-100 text-ink-500',
};

const pluralDni = (n: number) => (n === 1 ? 'dzień' : 'dni');

export function nearestBadge(daysUntil?: number | null, urgency?: string, label?: string | null) {
  if (daysUntil == null) return <span className="text-ink-300 text-[12px]">—</span>;
  const color = urgency === 'OVERDUE' ? 'text-red-500 font-semibold'
    : urgency === 'DUE_SOON' ? 'text-amber-600 font-semibold' : 'text-ink-600';
  const when = daysUntil === 0 ? 'dziś'
    : daysUntil > 0 ? `za ${daysUntil} ${pluralDni(daysUntil)}`
    : `${-daysUntil} ${pluralDni(-daysUntil)} po terminie`;
  return (
    <div className="flex flex-col leading-tight">
      <span className={'text-[12px] ' + color}>{when}</span>
      {label && <span className="text-[10px] text-ink-400 truncate max-w-[220px]" title={label}>{label}</span>}
    </div>
  );
}

export function KadryPage() {
  const { user } = useAuth();
  // EmployeeApi bramkuje mutacje pracownika na HR_SENSITIVE — front musi pytać o to samo,
  // inaczej rola z HR_MANAGE zobaczy przyciski i dostanie 403.
  const canEdit = hasPermission(user, 'HR_SENSITIVE');
  const navigate = useNavigate();

  const [items, setItems] = useState<Employee[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<EmploymentStatus | ''>('ACTIVE');
  const [modalItem, setModalItem] = useState<Employee | 'new' | null>(null);

  const reload = () => {
    setLoading(true);
    api.list(statusFilter || undefined)
      .then(setItems)
      .catch(e => setErr(e?.message ?? String(e)))
      .finally(() => setLoading(false));
  };
  useEffect(reload, [statusFilter]);

  const overdue = useMemo(() => items.filter(i => i.nearestUrgency === 'OVERDUE').length, [items]);
  const dueSoon = useMemo(() => items.filter(i => i.nearestUrgency === 'DUE_SOON').length, [items]);

  const onDelete = async (e: React.MouseEvent, emp: Employee) => {
    e.stopPropagation();
    if (!confirm(`Usunąć pracownika „${emp.fullName}"? Usunie to też jego umowy i kwalifikacje.`)) return;
    try { await api.remove(emp.id); reload(); }
    catch (er) { alert('Błąd: ' + (er instanceof Error ? er.message : String(er))); }
  };

  return (
    <div className="max-w-[1200px]">
      <header className="flex items-end justify-between mb-5 flex-wrap gap-3">
        <div>
          <h1>Kadry</h1>
          <div className="text-ink-400 text-[13px]">Pracownicy, umowy, badania lekarskie, szkolenia BHP i uprawnienia</div>
        </div>
        {canEdit && (
          <button onClick={() => setModalItem('new')} className="btn btn-primary btn-sm">
            <Icon name="plus" className="w-3.5 h-3.5"/> Dodaj pracownika
          </button>
        )}
      </header>

      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <div className="flex gap-1 flex-wrap">
          <button onClick={() => setStatusFilter('')}
            className={'btn btn-sm ' + (statusFilter === '' ? 'btn-primary' : 'btn-ghost')}>Wszyscy</button>
          {STATUSES.map(s => (
            <button key={s.id} onClick={() => setStatusFilter(s.id)}
              className={'btn btn-sm ' + (statusFilter === s.id ? 'btn-primary' : 'btn-ghost')}>{s.name}</button>
          ))}
        </div>
        {(overdue > 0 || dueSoon > 0) && (
          <div className="ml-auto flex items-center gap-2 text-[12px]">
            {overdue > 0 && <span className="pill pill-red">{overdue} po terminie</span>}
            {dueSoon > 0 && <span className="pill" style={{ background: '#fef3c7', color: '#b45309' }}>{dueSoon} wkrótce</span>}
          </div>
        )}
      </div>

      {err && <div className="text-red-500 mb-3 text-[12px]">Błąd: {err}</div>}
      {loading && <div className="text-ink-400">Ładowanie…</div>}

      {!loading && items.length === 0 && (
        <div className="card"><div className="card-body text-center text-ink-400 py-8">
          Brak pracowników w tym widoku.{canEdit ? ' Kliknij „Dodaj pracownika", aby wprowadzić pierwszego.' : ''}
        </div></div>
      )}

      {!loading && items.length > 0 && (
        <div className="card">
          <div className="card-body !p-0">
            <table className="table-hw">
              <thead>
                <tr>
                  <th>Pracownik</th>
                  <th className="w-48">Kontakt</th>
                  <th className="w-28">Status</th>
                  <th className="w-48">Najbliższy termin</th>
                  <th className="w-24"></th>
                </tr>
              </thead>
              <tbody>
                {items.map(e => (
                  <tr key={e.id} className="cursor-pointer hover:bg-ink-50/50" onClick={() => navigate(`/kadry/${e.id}`)}>
                    <td>
                      <div className="font-semibold text-ink-900">{e.fullName}</div>
                      <div className="text-[11px] text-ink-400">{e.position}{e.department ? ' · ' + e.department : ''}</div>
                    </td>
                    <td className="text-[12px] text-ink-500">
                      {e.phone && <div>{e.phone}</div>}
                      {e.email && <div className="truncate max-w-[180px]">{e.email}</div>}
                      {!e.phone && !e.email && '—'}
                    </td>
                    <td>
                      <span className={'inline-block px-2 py-0.5 rounded text-[11px] font-semibold ' + STATUS_PILL[e.employmentStatus]}>
                        {e.employmentStatusName}
                      </span>
                    </td>
                    <td>{nearestBadge(e.nearestDaysUntil, e.nearestUrgency, e.nearestDeadlineLabel)}</td>
                    <td>
                      <div className="flex items-center gap-1 justify-end">
                        {canEdit && (
                          <button onClick={ev => { ev.stopPropagation(); setModalItem(e); }}
                                  className="btn btn-ghost btn-icon text-ink-400 hover:text-water-600" title="Edytuj">
                            <Icon name="edit" className="w-3.5 h-3.5"/>
                          </button>
                        )}
                        {canEdit && (
                          <button onClick={ev => onDelete(ev, e)}
                                  className="btn btn-ghost btn-icon text-ink-400 hover:text-red-500" title="Usuń">
                            <Icon name="x" className="w-3.5 h-3.5"/>
                          </button>
                        )}
                        <Icon name="chevron" className="w-3.5 h-3.5 text-ink-300"/>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {modalItem && (
        <EmployeeModal
          item={modalItem === 'new' ? null : modalItem}
          onClose={() => setModalItem(null)}
          onSaved={() => { setModalItem(null); reload(); }}
        />
      )}
    </div>
  );
}

/** Modal tworzenia/edycji pracownika. Eksportowany — używany też na stronie szczegółów. */
export function EmployeeModal({ item, onClose, onSaved }: {
  item: Employee | null;
  onClose: () => void;
  onSaved: (saved: Employee) => void;
}) {
  const { user } = useAuth();
  const canSensitive = hasPermission(user, 'HR_SENSITIVE');
  const isEdit = !!item;

  const [firstName, setFirstName] = useState(item?.firstName ?? '');
  const [lastName, setLastName] = useState(item?.lastName ?? '');
  const [position, setPosition] = useState(item?.position ?? '');
  const [department, setDepartment] = useState(item?.department ?? '');
  const [phone, setPhone] = useState(item?.phone ?? '');
  const [email, setEmail] = useState(item?.email ?? '');
  const [userId, setUserId] = useState<number | ''>(item?.userId ?? '');
  const [companyId, setCompanyId] = useState<number | ''>(item?.companyId ?? '');
  const [employmentStatus, setEmploymentStatus] = useState<EmploymentStatus>(item?.employmentStatus ?? 'ACTIVE');
  const [hireDate, setHireDate] = useState(item?.hireDate ?? '');
  const [birthDate, setBirthDate] = useState(item?.birthDate ?? '');
  const [pesel, setPesel] = useState(item?.pesel ?? '');
  const [street, setStreet] = useState(item?.street ?? '');
  const [postalCode, setPostalCode] = useState(item?.postalCode ?? '');
  const [city, setCity] = useState(item?.city ?? '');
  const [emergencyContact, setEmergencyContact] = useState(item?.emergencyContact ?? '');
  const [notes, setNotes] = useState(item?.notes ?? '');

  const [companies, setCompanies] = useState<Company[]>([]);
  const [people, setPeople] = useState<AssignableUser[]>([]);
  const [meta, setMeta] = useState<EmployeeMeta | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    companiesApi.list().then(setCompanies).catch(() => {});
    usersApi.assignable().then(setPeople).catch(() => {});
    api.meta().then(setMeta).catch(() => {});
  }, []);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!firstName.trim() || !lastName.trim()) { setErr('Imię i nazwisko są wymagane'); return; }
    if (!position.trim()) { setErr('Stanowisko jest wymagane'); return; }
    setBusy(true); setErr(null);
    const payload: EmployeeInput = {
      firstName: firstName.trim(), lastName: lastName.trim(), position: position.trim(),
      department: department.trim() || null,
      phone: phone.trim() || null, email: email.trim() || null,
      userId: userId === '' ? null : Number(userId),
      companyId: companyId === '' ? null : Number(companyId),
      employmentStatus,
      hireDate: hireDate || null, birthDate: birthDate || null,
      pesel: canSensitive ? (pesel.trim() || null) : undefined,
      street: street.trim() || null, postalCode: postalCode.trim() || null, city: city.trim() || null,
      emergencyContact: emergencyContact.trim() || null,
      notes: notes.trim() || null,
    };
    try {
      const saved = isEdit ? await api.update(item!.id, payload) : await api.create(payload);
      onSaved(saved);
    } catch (er) {
      setErr(er instanceof Error ? er.message : String(er));
      setBusy(false);
    }
  };

  const statuses = meta?.employmentStatuses ?? STATUSES.map(s => ({ ...s, color: '' }));

  const { requestClose, confirmation, dialogProps } = useUnsavedChanges({
    value: { firstName, lastName, position, department, phone, email, userId, companyId, employmentStatus, hireDate, birthDate, pesel, street, postalCode, city, emergencyContact, notes }, onClose, busy,
  });

  return (
    <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4" onClick={requestClose} {...dialogProps}>
      {confirmation}
      <form onClick={e => e.stopPropagation()} onSubmit={submit}
            className="bg-white rounded-lg shadow-xl w-full max-w-[680px] max-h-[92vh] overflow-auto">
        <div className="px-5 py-3 border-b border-ink-100">
          <h2 className="text-[15px] font-semibold text-ink-900">{isEdit ? 'Edytuj pracownika' : 'Nowy pracownik'}</h2>
        </div>
        <div className="p-5 grid gap-3">
          <div className="grid grid-cols-2 gap-3">
            <label className="field"><span className="field-label">Imię *</span>
              <input value={firstName} onChange={e => setFirstName(e.target.value)} className="input"/></label>
            <label className="field"><span className="field-label">Nazwisko *</span>
              <input value={lastName} onChange={e => setLastName(e.target.value)} className="input"/></label>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <label className="field"><span className="field-label">Stanowisko *</span>
              <input value={position} onChange={e => setPosition(e.target.value)} className="input"
                     placeholder="np. Operator wiertnicy / Geolog"/></label>
            <label className="field"><span className="field-label">Dział</span>
              <input value={department} onChange={e => setDepartment(e.target.value)} className="input"/></label>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <label className="field"><span className="field-label">Telefon</span>
              <input value={phone} onChange={e => setPhone(e.target.value)} className="input"/></label>
            <label className="field col-span-2"><span className="field-label">E-mail</span>
              <input value={email} onChange={e => setEmail(e.target.value)} className="input"/></label>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <label className="field"><span className="field-label">Status</span>
              <SearchableSelect value={employmentStatus} onChange={v => setEmploymentStatus(v as EmploymentStatus)}>
                {statuses.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </SearchableSelect></label>
            <label className="field"><span className="field-label">Konto w portalu (opcjonalnie)</span>
              <SearchableSelect value={userId === '' ? '' : String(userId)} onChange={v => setUserId(v ? Number(v) : '')}>
                <option value="">— brak konta —</option>
                {people.map(p => <option key={p.id} value={p.id}>{p.fullName}</option>)}
              </SearchableSelect></label>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <label className="field"><span className="field-label">Data zatrudnienia</span>
              <input type="date" value={hireDate} onChange={e => setHireDate(e.target.value)} className="input"/></label>
            <label className="field"><span className="field-label">Data urodzenia</span>
              <input type="date" value={birthDate} onChange={e => setBirthDate(e.target.value)} className="input"/></label>
            {canSensitive && (
              <label className="field"><span className="field-label">PESEL</span>
                <input value={pesel} onChange={e => setPesel(e.target.value)} className="input" maxLength={11}/></label>
            )}
          </div>
          <div className="grid grid-cols-4 gap-3">
            <label className="field col-span-2"><span className="field-label">Ulica</span>
              <input value={street} onChange={e => setStreet(e.target.value)} className="input"/></label>
            <label className="field"><span className="field-label">Kod</span>
              <input value={postalCode} onChange={e => setPostalCode(e.target.value)} className="input" placeholder="00-000"/></label>
            <label className="field"><span className="field-label">Miejscowość</span>
              <input value={city} onChange={e => setCity(e.target.value)} className="input"/></label>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <label className="field"><span className="field-label">Kontakt awaryjny</span>
              <input value={emergencyContact} onChange={e => setEmergencyContact(e.target.value)} className="input"
                     placeholder="np. żona, 600 000 000"/></label>
            <label className="field"><span className="field-label">Firma (opcjonalnie)</span>
              <SearchableSelect value={companyId === '' ? '' : String(companyId)} onChange={v => setCompanyId(v ? Number(v) : '')}>
                <option value="">— brak —</option>
                {companies.map(c => <option key={c.id} value={c.id}>{c.shortName}</option>)}
              </SearchableSelect></label>
          </div>
          <label className="field"><span className="field-label">Notatki</span>
            <textarea value={notes} onChange={e => setNotes(e.target.value)} className="textarea" rows={2}/></label>

          {err && <div className="text-red-600 text-[12px] p-2 bg-red-50 border border-red-200 rounded">{err}</div>}

          <div className="flex justify-end gap-2 mt-1">
            <button type="button" onClick={requestClose} disabled={busy} className="btn btn-secondary">Anuluj</button>
            <button type="submit" disabled={busy} className="btn btn-primary">
              <Icon name="check" className="w-3.5 h-3.5"/> {busy ? 'Zapisywanie…' : (isEdit ? 'Zapisz' : 'Dodaj')}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}
